import {useCallback, useEffect, useRef, useState} from "react";
import type {IRoof, IRoofAddress, SearchLocation} from "../react-app-env";
import {defaultAddress, parseGeoAddress, parseGeoRoof} from "../utils/map";
import {maxArea, maxSlope, minArea, minSlope, roofTypesMap, supportedLanguages} from "../config/config";
import i18nEnglish from "../i18n/en";
import i18nSwiss from "../i18n/de-ch";
import i18nFrench from "../i18n/fr";
import i18nItalian from "../i18n/it";

interface PendingDeletion {
    draft: IRoofAddress;
    index: number;
    timeoutId: ReturnType<typeof setTimeout>;
}

const UNDO_TIMEOUT_MS = 5000;
const MAX_ROOFS = 3;

const useDrafts = () => {


    const [selectedDraft, setSelectedDraft] = useState<IRoofAddress>(defaultAddress);
    const [roofDrafts, setRoofDrafts] = useState<IRoofAddress[]>([{...defaultAddress}]);

    const [isChangingTabs, setIsChangingTabs] = useState(false);

    // Undo delete state
    const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);

    const removeDraftWithUndo = useCallback((id: number) => {
        setRoofDrafts(prevDrafts => {
            if (prevDrafts.length <= 1) return prevDrafts;

            const index = prevDrafts.findIndex(d => d.id === id);
            if (index === -1) return prevDrafts;

            const deletedDraft = prevDrafts[index];
            const newArr = prevDrafts.filter(d => d.id !== id);

            // Finalize any previous pending deletion
            setPendingDeletion(prev => {
                if (prev) clearTimeout(prev.timeoutId);
                return null;
            });

            // Set up new pending deletion with timeout
            const timeoutId = setTimeout(() => {
                setPendingDeletion(null);
            }, UNDO_TIMEOUT_MS);

            setPendingDeletion({ draft: deletedDraft, index, timeoutId });

            if (selectedDraft.id === id) {
                const newlySelected = newArr.find(d => d.id === (id - 1)) || newArr[0];
                setSelectedDraft(newlySelected);
            }

            return newArr;
        });
    }, [selectedDraft.id]);

    const undoRemoveDraft = useCallback(() => {
        if (!pendingDeletion) return;

        clearTimeout(pendingDeletion.timeoutId);
        const { draft, index } = pendingDeletion;

        setRoofDrafts(prevDrafts => {
            if (prevDrafts.length >= MAX_ROOFS) return prevDrafts;
            const insertIndex = Math.min(index, prevDrafts.length);
            const newArr = [...prevDrafts];
            newArr.splice(insertIndex, 0, draft);
            return newArr;
        });

        setSelectedDraft(draft);
        setPendingDeletion(null);
    }, [pendingDeletion]);

    const dismissUndo = useCallback(() => {
        if (pendingDeletion) {
            clearTimeout(pendingDeletion.timeoutId);
            setPendingDeletion(null);
        }
    }, [pendingDeletion]);

    // Cleanup undo timeout on unmount
    useEffect(() => () => {
        if (pendingDeletion?.timeoutId) clearTimeout(pendingDeletion.timeoutId);
    }, [pendingDeletion]);

    // Track when address changes to show visual feedback
    const [addressChanged, setAddressChanged] = useState(false);
    const addressGlowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerAddressGlow = () => {
        if (addressGlowTimer.current) clearTimeout(addressGlowTimer.current);
        setAddressChanged(true);
        addressGlowTimer.current = setTimeout(() => setAddressChanged(false), 2000);
    };

    // Cleanup glow timer on unmount
    useEffect(() => () => {
        if (addressGlowTimer.current) clearTimeout(addressGlowTimer.current);
    }, []);

    const typeaheadRef = useRef<any>(null);

    const updateDraft = (updatedDraft: IRoofAddress) => {
        setSelectedDraft(updatedDraft);
        updateDraftArray(updatedDraft);
    }


        const [errors, setErrors] = useState<Map<string, string>>(new Map());
        const addError = (key: string, message: string) => {
            setErrors((prevErrors) => new Map(prevErrors).set(key, message));
        };
    
        const removeError = (key: string) => {
            setErrors((prevErrors) => {
                const newErrors = new Map(prevErrors);
                newErrors.delete(key);
                return newErrors;
            });
        };


    let errs = new Map(errors);

    const updateDraftArray = (updatedDraft: IRoofAddress) => {
        setRoofDrafts(prevDrafts => {
            const exists = prevDrafts.some(existingItem => existingItem.id === updatedDraft.id);
            return exists
                ? prevDrafts.map(existingItem => existingItem.id === updatedDraft.id ? updatedDraft : existingItem)
                : [...prevDrafts, updatedDraft];
        });
        return updatedDraft;
    }

    const toggleCustomMode = (mode: boolean) => {
        setSelectedDraft(prevDraft => {
            let newRoof: IRoof = {
                roofId: Math.floor(Date.now() % 1e8),
                slope: 20,
                orientation: -1,
                length: 0,
                width: 0,
                area: 0,
                financialGain: 0,
                summerElectricityYield_kWh: 0,
                winterElectricityYield_kWh: 0,
                geometry: null,
                fieldsErrs: {
                    length: false,
                    width: false,
                    area: false,
                    slope: false,
                },
                isCustom: true
            };

            let customRoofs = prevDraft.roofs.filter(roof => roof.isCustom);

            return updateDraftArray({
                ...defaultAddress,
                type: "manual",
                label: 'Custom Roof',
                id: prevDraft.id,
                searchAddress: prevDraft.searchAddress,
                currentInput: prevDraft.currentInput,
                hasCustomRoof: mode,
                roofs: customRoofs?.length === 0 && mode ? [newRoof, ...prevDraft.roofs] : prevDraft.roofs
            })
        });
    };

    const i18nMap = {
            'de': i18nSwiss,
            'de-ch': i18nSwiss,
            'de-CH': i18nSwiss,
            'gsw': i18nSwiss,
            'en': i18nEnglish,
            'fr': i18nFrench,
            'it': i18nItalian,
          }[document.documentElement.getAttribute('data-language') || supportedLanguages[0]] || i18nEnglish;
    

    const handleSearchAddress = (newAddress: SearchLocation | any) => {
        setSelectedDraft(prevDraft => {

            const finalObj = {...prevDraft, searchAddress: newAddress};
            return updateDraftArray(finalObj);
        });
    }

    const selectRoofType = (roofTypeName: string) => {
        setSelectedDraft(prevDraft => updateDraftArray({...prevDraft, roofType: roofTypeName}));
    };

    const onAllowMultipleRoofs = (allowMultipleRoofs: boolean) => {
        setSelectedDraft((prevState) => {
            if (!allowMultipleRoofs && prevState.roofs?.length > 0) {
                return updateDraftArray({
                    ...prevState,
                    roofs: [prevState.roofs[0]],
                });
            }
            return updateDraftArray(prevState);
        });
    };

    const onClearRoofSelection = () => {
        setSelectedDraft((prevState) => {
            return updateDraftArray({
                ...prevState,
                roofs: []
            });
        });
    };

    const onUpdateRoofAddress = (geoAddress: any, geoRoof: any, address: SearchLocation | null, allowMultipleRoofs: boolean) => {
        try {
            const parsedAddress: IRoofAddress = parseGeoAddress(geoAddress) || defaultAddress;
            const newRoof: IRoof | null = parseGeoRoof(geoRoof);

            if (parsedAddress.label) {
                setSelectedDraft((prevState) => {
                    // If multiple roofs are allowed and the address matches
                    if (allowMultipleRoofs && prevState.label === parsedAddress.label) {
                        const existingRoofIndex = prevState.roofs.findIndex((roof) => roof.roofId === newRoof?.roofId);

                        return updateDraftArray({
                            ...prevState,
                            roofs: newRoof ? existingRoofIndex === -1
                            ? [...prevState.roofs, newRoof]  // Add new roof if it doesn't exist
                            : prevState.roofs.filter((roof) => roof.roofId !== newRoof.roofId)  // Remove existing roof
                            : prevState.roofs,
                        });
                    }

                    // Trigger visual feedback when address changes
                    if (prevState.label && prevState.label !== parsedAddress.label) {
                        triggerAddressGlow();
                    }

                    let customRoofs = prevState.roofs.filter(roof => roof.isCustom);

                    // Replace address and add new roof
                    return updateDraftArray({
                        ...parsedAddress,
                        searchAddress: address,
                        currentInput: address?.cleanLabel || '',
                        id: prevState.id,
                        roofs: newRoof ? customRoofs?.length > 0 ? [customRoofs[0], newRoof] : [newRoof] : [],
                    });
                });
            }
        } catch (error) {
            console.error("Error handling address and roof:", error);
        }
    }

    const onSelectAllBuildingRoofs = (geoAddress: any, geoRoofs: any[], address: SearchLocation | null) => {
        try {
            const parsedAddress: IRoofAddress = parseGeoAddress(geoAddress) || defaultAddress;
            const parsedRoofs: IRoof[] = geoRoofs
                .map(r => parseGeoRoof(r))
                .filter((r): r is IRoof => r !== null);

            if (parsedAddress.label && parsedRoofs.length > 0) {
                setSelectedDraft((prevState) => {
                    // Trigger visual feedback when address changes
                    if (prevState.label && prevState.label !== parsedAddress.label) {
                        triggerAddressGlow();
                    }
                    const customRoofs = prevState.roofs.filter(roof => roof.isCustom);
                    return updateDraftArray({
                        ...parsedAddress,
                        searchAddress: address,
                        currentInput: address?.cleanLabel || '',
                        id: prevState.id,
                        roofs: customRoofs.length > 0 ? [customRoofs[0], ...parsedRoofs] : parsedRoofs,
                    });
                });
            }
        } catch (error) {
            console.error("Error selecting all building roofs:", error);
        }
    };

    const addDraft = (draft?: IRoofAddress) => {
        setRoofDrafts(prevAddresses => {
                // Enforce 5-roof limit
                if (prevAddresses.length >= MAX_ROOFS) {
                    return prevAddresses;
                }
                const maxId = Math.max(...prevAddresses.map((roof) => roof.id));
                const finalDraft = draft ? draft : {...defaultAddress, id: maxId + 1};
                setSelectedDraft(finalDraft);
                return [...prevAddresses, finalDraft];
            }
        );
    }

    const canAddMoreRoofs = () => roofDrafts.length < MAX_ROOFS;

    const removeDraft = useCallback((id: number) => {
        setRoofDrafts(prevAddresses => {
            if (prevAddresses?.length > 1) {
                const newArr = prevAddresses.filter(roof => roof.id !== id);

                if (selectedDraft.id === id) {
                    const newlySelected = newArr.find(roof => roof.id === (id - 1)) || newArr[0];
                    setSelectedDraft(newlySelected);
                }

                return newArr;
            }
            return prevAddresses
        });
    }, [selectedDraft.id])

    const changeSelectedDraft = (e: any, roof: IRoofAddress) => {
        if (!e.target?.className?.includes('roof-tab-rm')) {
            setSelectedDraft(roof);
            setIsChangingTabs(true);
        }
    }

    const handleChangingTabs = (value: boolean) => {
        setIsChangingTabs(value);
    }

    const isDraftValid = (draft: IRoofAddress) => {
        if(draft.roofs?.length === 0) return false;

        if(draft.roofType === "unselected") {
            console.log(draft.roofType);
            errs.set(`${draft.id}:right`, i18nMap.selectRoofType);
            return false;  // Return false if roofType is "unselected"
        }


        if(draft.roofType === roofTypesMap[0].type) {
            console.log(draft.roofType);
            errs.set(`${draft.id}:right`, i18nMap.selectRoofType);
        } else errs.delete(`${draft.id}:right`);


        if(draft.hasCustomRoof) {
            let customRoof = draft.roofs.find(roof => roof.isCustom);

            if(!customRoof) return false;

            const slope = customRoof.slope;
            const area = customRoof.area;
            const orientation = customRoof.orientation;
            return slope >= minSlope && slope <= maxSlope && area >= minArea && area <= maxArea && orientation !== -1;
        } else return true;
    }

    const handleCurrentInputChange = (newVal: string) => {
        setSelectedDraft(prevDraft => {
            return updateDraftArray({...prevDraft, currentInput: newVal});
        });
    }

    return {
        selectedDraft,
        roofDrafts,
        changeSelectedDraft,
        typeaheadRef,
        updateDraft,
        toggleCustomMode,
        handleSearchAddress,
        selectRoofType,
        onAllowMultipleRoofs,
        onClearRoofSelection,
        onUpdateRoofAddress,
        onSelectAllBuildingRoofs,
        addDraft,
        removeDraft,
        removeDraftWithUndo,
        undoRemoveDraft,
        dismissUndo,
        pendingDeletion,
        isDraftValid,
        handleCurrentInputChange,
        handleChangingTabs,
        isChangingTabs,
        addressChanged,
        canAddMoreRoofs,
        maxRoofs: MAX_ROOFS,
    };
};

export default useDrafts;